/**
 * Shared request/response contract for the Inventory V2 read API, mounted at
 * /accounts/:accountId/inventory. The :accountId path param is the authz
 * context; the required `viewScope` body field is the lens being queried,
 * resolved and entitlement-checked server-side.
 *
 * Rows are at (location, product) grain. In the retailer flow, brand fields
 * mean POS Brand — NOT vendor/SparkPlug Brand.
 */
import { z } from 'zod';
import { objectIdRegex } from '../util/regex';

/**
 * The canonical stock-status vocabulary (API wire values, named after the UI
 * status legend). Single source of truth: the StockStatus type, request
 * validation, and STOCK_STATUSES iteration order all derive from it.
 */
export const StockStatuses = {
  OutOfStock: 'out-of-stock',
  LowStock: 'low-stock',
  InStock: 'in-stock',
  Overstock: 'overstock',
  Inactive: 'inactive',
  Stale: 'stale',
} as const;

export type StockStatus = typeof StockStatuses[keyof typeof StockStatuses];

/** Every stock status, in UI legend order. */
export const STOCK_STATUSES = Object.values(StockStatuses);

/** The four "live" statuses surfaced by default in inventory views. */
export const PRIMARY_STOCK_STATUSES = [
  StockStatuses.OutOfStock,
  StockStatuses.LowStock,
  StockStatuses.InStock,
  StockStatuses.Overstock,
] as const;

export type PrimaryStockStatus = typeof PRIMARY_STOCK_STATUSES[number];

/** "Old products" per the tech spec: stock that is no longer moving. */
export const OLD_STOCK_STATUSES = [StockStatuses.Stale, StockStatuses.Inactive] as const;

export type OldStockStatus = typeof OLD_STOCK_STATUSES[number];

export const isPrimaryStockStatus = (status: StockStatus): status is PrimaryStockStatus =>
  (PRIMARY_STOCK_STATUSES as readonly StockStatus[]).includes(status);

export const isOldStockStatus = (status: StockStatus): status is OldStockStatus =>
  (OLD_STOCK_STATUSES as readonly StockStatus[]).includes(status);

export const sortDirectionSchema = z.enum(['asc', 'desc']);

export type SortDirection = z.infer<typeof sortDirectionSchema>;

/** Search is a case-insensitive partial match on product name. */
export const inventoryFiltersSchema = z.object({
  locationIds: z.array(z.string()).optional(),
  brandIds: z.array(z.string()).optional(),
  productCategories: z.array(z.string()).optional(),
  productSubcategories: z.array(z.string()).optional(),
  stockStatuses: z.array(z.nativeEnum(StockStatuses)).optional(),
  search: z.string().max(200).optional(),
});

export type InventoryFilters = z.infer<typeof inventoryFiltersSchema>;

const MAX_LIMIT = 100;

/** Out-of-range pagination is rejected with a 400, not silently clamped. */
const paginationFields = {
  limit: z.number().int().min(1).max(MAX_LIMIT).optional(),
  offset: z.number().int().min(0).optional(),
};

const accountIdSchema = z.string().regex(objectIdRegex, 'must be a Mongo ObjectId');

export const inventoryAccountPathParamsSchema = z.object({
  accountId: accountIdSchema,
});

export type InventoryAccountPathParams = z.infer<typeof inventoryAccountPathParamsSchema>;

/**
 * The lens being queried, independent of the :accountId authz context. Today
 * only a retailer viewing itself (retailerId must match :accountId);
 * vendor-dashboard and spark-view scopes join this union later.
 */
export const inventoryViewScopeSchema = z.discriminatedUnion('scope', [
  z.object({
    scope: z.literal('retailer'),
    retailerId: accountIdSchema,
  }),
]);

export type InventoryViewScope = z.infer<typeof inventoryViewScopeSchema>;

export interface Paginated<T> {
  rows: T[];
  total: number;
  totalPages: number;
  limit: number;
  offset: number;
}

// --- By Product table ---

export const BY_PRODUCT_SORT_KEYS = [
  'productName',
  'onHandInventory',
  'stockStatus',
  'daysOnHand',
  'velocityPerDay',
  'lastSoldAt',
  'brandName',
  'productCategory',
  'locationName',
] as const;

export type ByProductSortKey = typeof BY_PRODUCT_SORT_KEYS[number];

export interface InventoryByProductRow {
  retailerId: string;
  retailerName: string | null;
  locationId: string;
  locationName: string | null;
  market: string | null;
  productId: string;
  internalKey: string | null;
  productName: string | null;
  productCategory: string | null;
  productSubcategory: string | null;
  brandId: string | null;
  brandName: string | null;
  brand: string | null;
  onHandInventory: number | null;
  /** Mapped server-side from the persisted value; unrecognized values become null. */
  stockStatus: StockStatus | null;
  daysOnHand: number | null;
  /** Daily velocity: units sold over the last 180 days / 180. */
  velocityPerDay: number | null;
  weeklyVelocity: number | null;
  l180Velocity: number | null;
  lastSoldAt: string | null;
  lastInventoryUpdate: string | null;
}

export const inventoryByProductBodySchema = z.object({
  viewScope: inventoryViewScopeSchema,
  filters: inventoryFiltersSchema.optional(),
  sort: z
    .object({
      sortBy: z.enum(BY_PRODUCT_SORT_KEYS),
      sortDirection: sortDirectionSchema.optional(),
    })
    .optional(),
  ...paginationFields,
});

export type InventoryByProductRequestBody = z.infer<typeof inventoryByProductBodySchema>;

export type InventoryByProductResponse = Paginated<InventoryByProductRow>;

// --- By Location rollup (shared by the table and the chart) ---

export const BY_LOCATION_SORT_KEYS = ['locationName', 'totalUnits', 'totalSkus'] as const;

export type ByLocationSortKey = typeof BY_LOCATION_SORT_KEYS[number];

export type ByLocationMetricSortKey = Extract<ByLocationSortKey, 'totalUnits' | 'totalSkus'>;

export const BY_LOCATION_METRIC_SORT_KEYS = ['totalUnits', 'totalSkus'] as const;

export interface InventoryByLocationRow {
  retailerId: string;
  retailerName: string | null;
  locationId: string;
  locationName: string | null;
  market: string | null;
  totalUnits: number;
  totalSkus: number;
  /** Densified per-status SKU counts: every StockStatus is present, zero-filled. */
  statusCounts: Record<StockStatus, number>;
}

/** Grand totals across ALL filtered locations (not just the current page). */
export interface InventoryByLocationSummary {
  totalUnits: number;
  totalSkus: number;
}

export interface InventoryByLocationResponse extends Paginated<InventoryByLocationRow> {
  summary: InventoryByLocationSummary;
}

export const inventoryByLocationBodySchema = z.object({
  viewScope: inventoryViewScopeSchema,
  filters: inventoryFiltersSchema.optional(),
  sort: z
    .object({
      sortBy: z.enum(BY_LOCATION_SORT_KEYS),
      sortDirection: sortDirectionSchema.optional(),
    })
    .optional(),
  ...paginationFields,
});

export type InventoryByLocationRequestBody = z.infer<typeof inventoryByLocationBodySchema>;

export const inventoryByLocationChartBodySchema = z.object({
  viewScope: inventoryViewScopeSchema,
  filters: inventoryFiltersSchema.optional(),
  /** Metric to order by; the chart always sorts descending. Defaults to totalUnits. */
  sortBy: z.enum(BY_LOCATION_METRIC_SORT_KEYS).optional(),
  ...paginationFields,
});

export type InventoryByLocationChartRequestBody = z.infer<
  typeof inventoryByLocationChartBodySchema
>;

// --- Filter options ---

export interface InventoryFilterOption {
  id: string;
  name: string;
}

export interface InventoryFilterOptionsResponse {
  locations: InventoryFilterOption[];
  brands: InventoryFilterOption[];
  productCategories: string[];
  productSubcategories: string[];
}

export const inventoryFilterOptionsBodySchema = z.object({
  viewScope: inventoryViewScopeSchema,
});

export type InventoryFilterOptionsRequestBody = z.infer<typeof inventoryFilterOptionsBodySchema>;
